import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPinOffIcon, RefreshCwIcon, TriangleAlertIcon } from 'lucide-react';
import { Button, EmptyState, Modal, Skeleton, Spinner } from '@noor/ui';
import { type ModalRenderProps } from '@noor/lib';
import { useYmaps } from '@noor/maps';
import { Can } from '@/entities/access';
import {
  offerCatalog,
  offersBounds,
  plotOffers,
  toLngLat,
  useOrderDetail,
  useOrderOffers,
  type OfferFilters,
  type OffersMode,
} from '@/entities/order';
import { buildOfferMarkers } from '../lib/offer-markers';
import { useOffersModeStore } from '../model/offers-mode-store';
import { useOrderOffer } from '../model/use-order-offer';
import { OfferDetailsCard } from './offer-details-card';
import { OfferStatsBar } from './offer-stats-bar';
import { OffersModeSwitch } from './offers-mode-switch';
import { OffersCanvasOsm } from './offers-canvas-osm';
import { OffersCanvasYandex } from './offers-canvas-yandex';

/** Near-full-screen: the map is the content, so give it room. */
const MODAL_CLASS =
  'h-[min(46rem,calc(100dvh-2rem))] grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden sm:max-w-[min(72rem,calc(100%-2rem))]';

/**
 * Order offers visualization (cpanel `LogsInfo` → "Визуализация"): where every
 * courier that was offered this order stood, and what each one did with it.
 * Stays live — the socket `refresh` push invalidates the orders cache, which
 * refetches the offers while the modal is open.
 *
 * Two modes feed the same map: `legacy` (default) reads cpanel's own API and
 * rules, `new` the Delivery service. The operator's choice is remembered.
 */
export function OrderOffersModal({ payload }: ModalRenderProps<'order-offers'>) {
  return (
    <Can permission="orders.detail.view">
      <OrderOffersContent orderId={payload.orderId} />
    </Can>
  );
}

function OrderOffersContent({ orderId }: { orderId: string | number }) {
  const { t } = useTranslation('orders');
  const { bindings, isDisabled } = useYmaps();
  const mode = useOffersModeStore((state) => state.mode);
  const setStoredMode = useOffersModeStore((state) => state.setMode);
  const orderQuery = useOrderDetail(orderId);
  const offersQuery = useOrderOffers(orderId, mode);
  const order = orderQuery.data;
  // The query key carries the mode, so a view always matches its reason table.
  const view = offersQuery.data;
  const catalog = offerCatalog(mode);
  // The courier the live search is offering this order to right now (stage 6).
  const liveOffer = useOrderOffer(orderId, order?.stage ?? 0);

  const [filters, setFilters] = useState<OfferFilters>({});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Filters are keyed by reason ids and a selection by a marker of the old
  // source: neither means anything once the data behind the map changes.
  const changeMode = (next: OffersMode) => {
    if (next === mode) return;
    setFilters({});
    setSelectedKey(null);
    setStoredMode(next);
  };

  const pickup = order?.origin?.[0]?.location;
  const records = view?.records;
  const { plotted, unplottedCount } = useMemo(() => plotOffers(records), [records]);
  // Framing covers every plotted courier, independent of the legend filter, so
  // toggling a chip never changes what "fit" means.
  const bounds = useMemo(() => offersBounds(pickup, plotted), [pickup, plotted]);
  const markers = useMemo(
    () =>
      buildOfferMarkers({
        plotted,
        origin: pickup,
        filters,
        catalog,
        activeCourierId: liveOffer?.id,
      }),
    [plotted, pickup, filters, catalog, liveOffer?.id],
  );
  const selected = markers.find((marker) => marker.key === selectedKey) ?? null;
  const pickupCoords = toLngLat(pickup);

  const offerCount = records?.length ?? 0;
  const refreshing = offersQuery.isFetching && !offersQuery.isPending;
  const displayId = order?.display_id ?? orderId;

  const description = (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <span>{t('offers.order', { id: displayId })}</span>
      {view ? (
        <>
          <span aria-hidden>·</span>
          <span>{t('offers.count', { count: offerCount })}</span>
        </>
      ) : null}
      {refreshing ? <Spinner size="xs" label={t('offers.updating')} /> : null}
    </span>
  );

  let content: React.ReactNode;
  if (!order || !view) {
    content =
      orderQuery.isError || offersQuery.isError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border px-6 text-center">
          <TriangleAlertIcon className="size-8 text-destructive" />
          <p className="text-sm text-muted-foreground">{t('offers.error')}</p>
          <Button
            variant="soft"
            size="modal"
            shape="rounded"
            onClick={() => {
              void orderQuery.refetch();
              void offersQuery.refetch();
            }}
          >
            <RefreshCwIcon />
            {t('offers.retry')}
          </Button>
        </div>
      ) : (
        <Skeleton className="min-h-0 flex-1 rounded-xl" aria-busy />
      );
  } else {
    let map: React.ReactNode;
    if (!bounds || !pickupCoords) {
      map = (
        <div className="grid h-full place-items-center p-4">
          <EmptyState
            icon={MapPinOffIcon}
            title={t('offers.noPickup')}
            className="border-none bg-transparent"
          />
        </div>
      );
    } else {
      const canvas = {
        origin: { coords: pickupCoords, label: `#${displayId}` },
        markers,
        bounds,
        // A new mode brings new courier positions: reframe instead of keeping
        // the previous source's viewport.
        fitKey: `${orderId}:${mode}`,
        selectedKey,
        onSelect: setSelectedKey,
        className: 'absolute inset-0',
      };
      map = isDisabled ? (
        <OffersCanvasOsm {...canvas} />
      ) : bindings ? (
        <OffersCanvasYandex bindings={bindings} {...canvas} />
      ) : (
        <div className="grid h-full place-items-center">
          <Spinner size="lg" label={t('offers.loadingMap')} />
        </div>
      );
    }

    content = (
      <div
        className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-muted/40"
        role="region"
        aria-label={t('offers.mapLabel')}
      >
        {map}
        {bounds && offerCount === 0 ? (
          <p className="pointer-events-none absolute bottom-3 left-1/2 z-[1000] -translate-x-1/2 rounded-full bg-card px-4 py-2 text-sm whitespace-nowrap text-muted-foreground shadow-md">
            {t('offers.empty')}
          </p>
        ) : null}
        {bounds && unplottedCount > 0 ? (
          <p className="pointer-events-none absolute top-3 left-3 z-[1000] max-w-[60%] rounded-full bg-card px-3 py-1.5 text-xs text-muted-foreground shadow-md">
            {t('offers.unplotted', { count: unplottedCount })}
          </p>
        ) : null}
        {selected ? (
          <OfferDetailsCard marker={selected} onClose={() => setSelectedKey(null)} />
        ) : null}
      </div>
    );
  }

  return (
    <Modal title={t('offers.title')} description={description} className={MODAL_CLASS}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
        {/* Always rendered, so the operator can switch source even while one
            is loading or failing. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <OffersModeSwitch value={mode} onChange={changeMode} />
          {view && view.stats.length > 0 ? (
            <div className="min-w-0 flex-1">
              <OfferStatsBar
                stats={view.stats}
                filters={filters}
                onToggle={(key) => setFilters((prev) => ({ ...prev, [key]: !prev[key] }))}
                onClear={() => setFilters({})}
              />
            </div>
          ) : null}
        </div>
        {content}
      </div>
    </Modal>
  );
}
